/**
 * ChatbotContext
 * Centralized context for managing Assets state and artifacts
 * across ChatbotContainer and ChatbotInput components
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Asset } from '@customTypes/asset';
import { Artifact, assetToArtifact } from '@customTypes/artifact';
import { useChatbotStoreContext } from '@stores/chatbot/ChatbotStoreContext';
import { useRecentArtifactsStore } from '@stores/recentArtifactsStore';
import { AssetUploadState } from '@apis/queries/useAssetsv2';

export interface ChatbotContextValue {
	addedToChatAssets: Asset[];

	// Artifact state - unified way to reference entities (campaigns, assets, etc.) in chat
	chatArtifacts: Artifact[];
	addArtifact: (artifact: Artifact) => void;
	removeArtifact: (index: number) => void;
	clearArtifacts: () => void;

	// Input position for message fly-in animation
	inputRect: DOMRect | null;
	setInputRect: (rect: DOMRect | null) => void;

	// Shared handlers
	handleAssetDeletedFromHub: (fileId: string) => void;
	registerSetUploadedAssets: (setter: React.Dispatch<React.SetStateAction<AssetUploadState[]>>) => void;
	addAssetsToChat: (assets: Asset[]) => void;
	addAssetToChat: (asset: Asset) => void;
	removeAssetFromChat: (assetId: string) => void;
	clearAssetsFromChat: () => void;
}

const ChatbotContext = createContext<ChatbotContextValue | null>(null);

interface ChatbotProviderProps {
	children: React.ReactNode;
}

export const ChatbotProvider: React.FC<ChatbotProviderProps> = ({ 
	children
}) => {
	const setUploadedAssetsRef = useRef<React.Dispatch<React.SetStateAction<AssetUploadState[]>> | null>(null);
	const closeAssetHubModal = useChatbotStoreContext((s) => s.closeAssetHubModal);
	const conversationId = useChatbotStoreContext((s) => s.conversationId);
	const [addedToChatAssets, setAddedToChatAssets] = useState<Asset[]>([]);
	const [chatArtifacts, setChatArtifacts] = useState<Artifact[]>([]);
	const [inputRect, setInputRect] = useState<DOMRect | null>(null);

	const removeAssetFromChat = useCallback((assetId: string) => {
		setAddedToChatAssets(prev => prev.filter(asset => asset.asset_id !== assetId));
	}, []);

	const clearAssetsFromChat = useCallback(() => {
		setAddedToChatAssets([]);
	}, []);

	// Artifact management - unified way to add entities (campaigns, assets, etc.) to chat
	const addArtifact = useCallback((artifact: Artifact) => {
		setChatArtifacts(prev => {
			// Deduplicate: only add if not already present (same type + id)
			const isDuplicate = prev.some(
				existing => existing.type === artifact.type && existing.id === artifact.id
			);
			return isDuplicate ? prev : [...prev, artifact];
		});
		// Every attach surface (drag-drop, AssetModal, picker, Report iterate,
		// sidebar "Add to chat") flows through here. Marking interaction at this
		// chokepoint covers them all without per-surface plumbing.
		useRecentArtifactsStore.getState().markInteracted(artifact);
	}, []);

	const removeArtifact = useCallback((index: number) => {
		setChatArtifacts(prev => prev.filter((_, i) => i !== index));
	}, []);

	const clearArtifacts = useCallback(() => {
		setChatArtifacts([]);
	}, []);

	// Clear assets and artifacts when a new conversation starts
	useEffect(() => {
		if (!conversationId) {
			clearAssetsFromChat();
			clearArtifacts();
		}
	}, [conversationId, clearAssetsFromChat, clearArtifacts]);

	// Consume pending artifacts queued from outside the context (e.g. Report iterate button)
	const pendingArtifacts = useChatbotStoreContext((s) => s.pendingArtifacts);
	const clearPendingArtifacts = useChatbotStoreContext((s) => s.clearPendingArtifacts);
	useEffect(() => {
		if (pendingArtifacts.length > 0) {
			pendingArtifacts.forEach(addArtifact);
			clearPendingArtifacts();
		}
	}, [pendingArtifacts, addArtifact, clearPendingArtifacts]);

	// Unified method to add assets to chat - converts to artifacts
	const addAssetsToChat = useCallback((assets: Asset[]) => {
		// Convert assets to artifacts and add them
		assets.forEach(asset => {
			const artifact = assetToArtifact(asset);
			addArtifact(artifact);
		});
		// Keep legacy state for backward compatibility
		setAddedToChatAssets(prev => [...prev, ...assets]);
		closeAssetHubModal();
	}, [addArtifact, closeAssetHubModal]);

	// Method to add a single asset to chat - converts to artifact
	const addAssetToChat = useCallback((asset: Asset) => {
		// Convert asset to artifact and add
		const artifact = assetToArtifact(asset);
		addArtifact(artifact);
		// Keep legacy state for backward compatibility
		setAddedToChatAssets(prev => [...prev, asset]);
	}, [addArtifact]);

	const handleAssetDeletedFromHub = useCallback((assetId: string) => {
		// Remove from addedToChatAssets if present
		setAddedToChatAssets(prev => prev.filter(asset => asset.asset_id !== assetId));
		// Remove from chatArtifacts if present (where type is 'asset' and id matches assetId)
		setChatArtifacts(prev => prev.filter(artifact => !(artifact.type === 'asset' && artifact.id === assetId)));
	}, []);

	const value: ChatbotContextValue = {
		addedToChatAssets,
		inputRect,
		setInputRect,
		chatArtifacts,
		addArtifact,
		removeArtifact,
		clearArtifacts,
		handleAssetDeletedFromHub,
		registerSetUploadedAssets: (setter) => {
			setUploadedAssetsRef.current = setter;
		},
		addAssetsToChat,
		addAssetToChat,
		removeAssetFromChat,
		clearAssetsFromChat,
	};

	return (
		<ChatbotContext.Provider value={value}>
			{children}
		</ChatbotContext.Provider>
	);
};

export const useChatbot = (): ChatbotContextValue => {
	const context = useContext(ChatbotContext);
	if (!context) {
		throw new Error('useChatbot must be used within ChatbotProvider');
	}
	return context;
};
